import {
    Badge, Box, Button, ButtonGroup, Divider, Flex, GridItem, HStack,
    IconButton as ChakraIconButton, SimpleGrid, Spinner, Switch, Td, Text, Tooltip,
    useColorModeValue, useToast, VStack,
    Select as ChakraSelect,
} from "@chakra-ui/react"
import { Alert, Breadcrumb, Container, Heading, Icon, Profile, Row, Searchable, Show, Table, Tag } from "components"
import { Moment, API } from "config";
import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router";
import PropTypes from 'prop-types';

const SYSTEMS = [
    { key: 'consultant',  label: 'Consultores', icon: 'consultant',  color: 'orange', gradient: 'linear(to-br, orange.400, orange.600)' },
    { key: 'distributor', label: 'Núcleos',     icon: 'distributor', color: 'blue',   gradient: 'linear(to-br, blue.400, blue.600)'   },
    { key: 'employee',    label: 'Backoffice',  icon: 'employee',    color: 'purple', gradient: 'linear(to-br, purple.400, purple.600)' },
]

const TYPE_MAP = {
    'App\\Models\\Customer':     { system: 'consultant',  label: 'Consultores' },
    'App\\Models\\Distributor':  { system: 'distributor', label: 'Núcleos'     },
    'App\\Models\\Employee':     { system: 'employee',    label: 'Backoffice'  },
}

export default function Sessions() {
    const toast = useToast()

    const [data, setData]           = useState([])
    const [meta, setMeta]           = useState(null)
    const [summary, setSummary]     = useState({ consultant: 0, distributor: 0, employee: 0, total: 0 })
    const [maintenance, setMaintenance] = useState({ global: false, consultant: false, distributor: false })
    const [whitelist, setWhitelist] = useState([])
    const [loading, setLoading]     = useState(false)
    const [actionLoading, setActionLoading] = useState(null)
    const [page, setPage]           = useState(1)
    const [term, setTerm]           = useState(null)
    const [system, setSystem]       = useState(null)
    const [filterKey, setFilterKey] = useState(0)

    const cardBg      = useColorModeValue('white', 'gray.800')
    const cardBorder  = useColorModeValue('gray.100', 'gray.700')
    const filterBg    = useColorModeValue('gray.50', 'gray.700')
    const filterBorderC = useColorModeValue('gray.100', 'gray.600')
    const labelColor  = useColorModeValue('gray.400', 'gray.500')

    const hasActiveFilters = !!(term || system)

    const clearFilters = () => {
        setTerm(null); setSystem(null); setPage(1); setFilterKey(k => k + 1)
    }

    const fetchAll = useCallback(async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams({ page })
            if (term)   params.set('term', term)
            if (system) params.set('system', system)

            const [sessions, status, counts, wl] = await Promise.all([
                API.get(`/sessions/active?${params}`),
                API.get('/sessions/maintenance-status'),
                API.get('/sessions/summary'),
                API.get('/sessions/whitelist'),
            ])

            setData(sessions.data.data)
            setMeta(sessions.data.meta)
            setMaintenance({
                global:      status.data.maintenance,
                consultant:  status.data.maintenance_consultant,
                distributor: status.data.maintenance_distributor,
            })
            setSummary(counts.data)
            setWhitelist(wl.data)
        } catch {
            toast({ title: 'Erro ao carregar dados', status: 'error' })
        }
        setLoading(false)
    }, [toast, page, term, system])

    const handleLogoutAll = async () => {
        if (!window.confirm('Deslogar absolutamente todos os usuários do sistema?')) return
        setActionLoading('logout-all')
        try {
            await API.post('/sessions/logout-all')
            toast({ title: 'Todos os usuários foram desconectados.', status: 'success' })
            fetchAll()
        } catch {
            toast({ title: 'Erro na operação', status: 'warning' })
        }
        setActionLoading(null)
    }

    const handleLogoutSystem = async (sys) => {
        const label = SYSTEMS.find(s => s.key === sys)?.label ?? sys
        if (!window.confirm(`Deslogar todos os usuários de ${label}?`)) return
        setActionLoading(`logout-${sys}`)
        try {
            await API.post('/sessions/logout-system', { system: sys })
            toast({ title: `Usuários de ${label} desconectados.`, status: 'success' })
            fetchAll()
        } catch {
            toast({ title: 'Erro ao deslogar', status: 'warning' })
        }
        setActionLoading(null)
    }

    const handleToggleMaintenance = async (sys = null) => {
        const label = sys ? (SYSTEMS.find(s => s.key === sys)?.label ?? sys) : 'geral'
        const isOn  = sys ? maintenance[sys] : maintenance.global
        const msg   = isOn
            ? `Liberar acesso ${sys ? `para ${label}` : 'para todos'}?`
            : `Ativar manutenção ${sys ? `de ${label}` : 'geral'}? Usuários serão deslogados.`
        if (!window.confirm(msg)) return

        setActionLoading(`maint-${sys ?? 'global'}`)
        try {
            const payload = sys ? { system: sys } : {}
            const res = await API.post('/sessions/maintenance-toggle', payload)
            toast({ title: res.data.message, status: isOn ? 'success' : 'warning' })
            fetchAll()
        } catch {
            toast({ title: 'Erro ao alterar manutenção', status: 'error' })
        }
        setActionLoading(null)
    }

    const handleAddToWhitelist = async (sessionItem) => {
        const typeInfo = TYPE_MAP[sessionItem.tokenable_type] ?? { system: 'unknown', label: 'Outro' }
        try {
            await API.post('/sessions/whitelist', {
                raw_tokenable_id: sessionItem.raw_tokenable_id,
                tokenable_type:   sessionItem.tokenable_type,
                name:             sessionItem.user_name,
                email:            sessionItem.user_email,
                system:           typeInfo.system,
                label:            typeInfo.label,
            })
            toast({ title: `${sessionItem.user_name} adicionado à lista de protegidos.`, status: 'success' })
            fetchAll()
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'Erro ao proteger usuário'
            toast({ title: msg, status: 'warning' })
        }
    }

    const handleRemoveFromWhitelist = async (rawId, name) => {
        if (!window.confirm(`Remover ${name} da lista de protegidos?`)) return
        try {
            await API.delete(`/sessions/whitelist/${rawId}`)
            toast({ title: `${name} removido da proteção.`, status: 'success' })
            fetchAll()
        } catch {
            toast({ title: 'Erro ao remover proteção', status: 'error' })
        }
    }

    useEffect(() => { setPage(1) }, [term, system])
    useEffect(() => { fetchAll() }, [fetchAll])

    const whitelistedIds = whitelist.map(w => w.raw_tokenable_id)

    const cols = [
        { children: 'Usuário' }, { children: 'Tipo' },
        { children: 'Atividade' }, { children: '', textAlign: 'right' }
    ]

    return (
        <Container>
            <Breadcrumb prev={{ path: '/tools', label: 'Ferramentas' }} current="Sessões" />

            <Heading title="Gerenciamento de Sessões">
                <ButtonGroup size="sm">
                    <Button
                        leftIcon={<Icon name="refresh" />}
                        onClick={fetchAll}
                        isLoading={loading}
                        variant="ghost"
                    >
                        Atualizar
                    </Button>
                </ButtonGroup>
            </Heading>

            <Show visible={maintenance.global}>
                <Alert my={4} status="warning" variant="solid" borderRadius="md">
                    <b>MANUTENÇÃO GLOBAL ATIVA:</b> Sistema bloqueado para consultores e distribuidores.
                </Alert>
            </Show>

            {/* Cards de sistema */}
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4} my={6}>

                {/* Card total */}
                <SystemCard
                    label="Total Online"
                    icon="plug"
                    count={summary.total}
                    gradient="linear(to-br, gray.500, gray.700)"
                    color="gray"
                    cardBg={cardBg}
                    cardBorder={cardBorder}
                    loading={loading}
                    actionLoading={actionLoading}
                    onLogout={handleLogoutAll}
                    logoutKey="logout-all"
                    logoutLabel="Deslogar Geral"
                    maintenanceValue={maintenance.global}
                    onToggleMaintenance={() => handleToggleMaintenance(null)}
                    maintenanceKey="maint-global"
                    maintenanceLabel="Manutenção Global"
                />

                {/* Cards por sistema */}
                {SYSTEMS.map(s => (
                    <SystemCard
                        key={s.key}
                        label={s.label}
                        icon={s.icon}
                        count={summary[s.key]}
                        gradient={s.gradient}
                        color={s.color}
                        cardBg={cardBg}
                        cardBorder={cardBorder}
                        loading={loading}
                        actionLoading={actionLoading}
                        onLogout={() => handleLogoutSystem(s.key)}
                        logoutKey={`logout-${s.key}`}
                        logoutLabel={`Deslogar ${s.label}`}
                        maintenanceValue={s.key !== 'employee' ? maintenance[s.key] : null}
                        onToggleMaintenance={s.key !== 'employee' ? () => handleToggleMaintenance(s.key) : null}
                        maintenanceKey={`maint-${s.key}`}
                        maintenanceLabel="Manutenção"
                    />
                ))}
            </SimpleGrid>

            {/* Painel da whitelist */}
            <WhitelistPanel
                whitelist={whitelist}
                onRemove={handleRemoveFromWhitelist}
                cardBg={cardBg}
                cardBorder={cardBorder}
                loading={loading}
            />

            <Divider mb={4} />

            {/* Painel de filtros */}
            <Box bg={filterBg} border="1px solid" borderColor={filterBorderC} borderRadius="xl" p={4} mb={4}>
                <HStack justify="space-between" align="center" mb={3}>
                    <HStack spacing={2}>
                        <Icon name="search" boxSize={3.5} color={labelColor} />
                        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="widest" color={labelColor}>
                            Filtros
                        </Text>
                        {hasActiveFilters && <Box w="6px" h="6px" borderRadius="full" bg="#008AAC" />}
                    </HStack>
                    {hasActiveFilters && (
                        <Button
                            size="sm" onClick={clearFilters} borderRadius="full" px={4} h="28px"
                            fontSize="xs" fontWeight="semibold" colorScheme="red" variant="ghost"
                            leftIcon={<Icon name="close" boxSize={3} />}
                        >
                            Limpar filtros
                        </Button>
                    )}
                </HStack>

                <SimpleGrid key={filterKey} columns={{ base: 1, md: 4 }} gap={2}>
                    <GridItem colSpan={{ base: 1, md: 3 }}>
                        <Searchable value={term} setValue={setTerm} placeholder="Buscar por nome..." />
                    </GridItem>
                    <GridItem>
                        <HStack mt={4} borderRadius={4} p={2} bg="rgba(0,0,0,0.1)">
                            <ChakraSelect
                                border={0} bg="transparent" fontWeight="bold"
                                _focus={{ boxShadow: 'none' }}
                                value={system ?? ''}
                                onChange={e => setSystem(e.target.value || null)}
                            >
                                <option value="">Todos os sistemas</option>
                                <option value="consultant">Consultores</option>
                                <option value="distributor">Núcleos</option>
                                <option value="employee">Backoffice</option>
                            </ChakraSelect>
                        </HStack>
                    </GridItem>
                </SimpleGrid>
            </Box>

            <Table columns={cols} meta={meta} setPage={setPage} loading={loading}>
                {data?.map(item => (
                    <SessionRow
                        key={item.id}
                        data={item}
                        onRefresh={fetchAll}
                        isProtected={whitelistedIds.includes(item.raw_tokenable_id)}
                        onProtect={handleAddToWhitelist}
                    />
                ))}
            </Table>
        </Container>
    )
}

// ─── Painel da whitelist ─────────────────────────────────────────────────────

const WhitelistPanel = ({ whitelist, onRemove, cardBg, cardBorder, loading }) => {
    const panelBg     = useColorModeValue('teal.50', 'teal.900')
    const panelBorder = useColorModeValue('teal.200', 'teal.700')
    const labelColor  = useColorModeValue('teal.600', 'teal.300')
    const emptyColor  = useColorModeValue('teal.400', 'teal.500')
    const chipBg      = useColorModeValue('white', 'teal.800')
    const chipBorder  = useColorModeValue('teal.200', 'teal.600')

    const systemColor = (sys) => {
        if (sys === 'consultant')  return 'orange'
        if (sys === 'distributor') return 'blue'
        if (sys === 'employee')    return 'purple'
        return 'gray'
    }

    return (
        <Box
            bg={panelBg}
            border="1px solid"
            borderColor={panelBorder}
            borderRadius="2xl"
            p={4}
            mb={4}
        >
            <HStack spacing={2} mb={3}>
                <Icon name="identity" boxSize={4} color={labelColor} />
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="widest" color={labelColor}>
                    Usuários Protegidos
                </Text>
                <Badge colorScheme="teal" borderRadius="full" fontSize="0.6rem" px={2}>
                    {whitelist.length}
                </Badge>
                <Tooltip
                    label="Usuários nesta lista nunca serão deslogados por operações em massa (logout geral, por sistema ou ativação de manutenção)."
                    fontSize="xs"
                    hasArrow
                    placement="right"
                >
                    <Box cursor="help">
                        <Icon name="info" boxSize={3.5} color={labelColor} opacity={0.6} />
                    </Box>
                </Tooltip>
            </HStack>

            {loading && whitelist.length === 0 ? (
                <Flex align="center" justify="center" py={4}>
                    <Spinner size="sm" color="teal.400" />
                </Flex>
            ) : whitelist.length === 0 ? (
                <Flex align="center" gap={2} py={2} opacity={0.6}>
                    <Icon name="roles" boxSize={4} color={emptyColor} />
                    <Text fontSize="sm" color={emptyColor}>
                        Nenhum usuário protegido. Clique no escudo ao lado de um usuário na tabela abaixo para protegê-lo.
                    </Text>
                </Flex>
            ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={2}>
                    {whitelist.map(entry => (
                        <Box
                            key={entry.raw_tokenable_id}
                            bg={chipBg}
                            border="1px solid"
                            borderColor={chipBorder}
                            borderRadius="xl"
                            px={3}
                            py={2}
                        >
                            <Flex justify="space-between" align="center">
                                <HStack spacing={2} flex={1} minW={0}>
                                    <Icon name="identity" boxSize={4} color="teal.500" flexShrink={0} />
                                    <Box minW={0}>
                                        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{entry.name}</Text>
                                        <HStack spacing={1}>
                                            <Badge colorScheme={systemColor(entry.system)} fontSize="0.6rem" borderRadius="full" px={2}>
                                                {entry.label ?? entry.system}
                                            </Badge>
                                            {entry.email && (
                                                <Text fontSize="xs" color="gray.500" noOfLines={1}>{entry.email}</Text>
                                            )}
                                        </HStack>
                                    </Box>
                                </HStack>
                                <Tooltip label="Remover proteção" fontSize="xs" hasArrow>
                                    <ChakraIconButton
                                        icon={<Icon name="close" boxSize={3} />}
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        aria-label="Remover proteção"
                                        onClick={() => onRemove(entry.raw_tokenable_id, entry.name)}
                                        flexShrink={0}
                                        ml={2}
                                    />
                                </Tooltip>
                            </Flex>
                        </Box>
                    ))}
                </SimpleGrid>
            )}
        </Box>
    )
}

WhitelistPanel.propTypes = {
    whitelist: PropTypes.array.isRequired,
    onRemove: PropTypes.func.isRequired,
    cardBg: PropTypes.string,
    cardBorder: PropTypes.string,
    loading: PropTypes.bool,
}

// ─── Card de sistema ─────────────────────────────────────────────────────────

const SystemCard = ({
    label, icon, count, gradient, color, cardBg, cardBorder,
    loading, actionLoading,
    onLogout, logoutKey, logoutLabel,
    maintenanceValue, onToggleMaintenance, maintenanceKey, maintenanceLabel,
}) => {
    const isLogoutLoading = actionLoading === logoutKey
    const isMaintenanceLoading = actionLoading === maintenanceKey
    const hasMaintenance = maintenanceValue !== null && maintenanceValue !== undefined

    return (
        <Box
            bg={cardBg}
            border="1px solid"
            borderColor={hasMaintenance && maintenanceValue ? `${color}.300` : cardBorder}
            borderRadius="2xl"
            overflow="hidden"
            boxShadow={hasMaintenance && maintenanceValue ? `0 0 0 2px var(--chakra-colors-${color}-300)` : 'sm'}
            transition="all 0.2s"
            _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }}
        >
            {/* Header colorido */}
            <Box bgGradient={gradient} px={4} py={3}>
                <HStack justify="space-between" align="center">
                    <HStack spacing={2}>
                        <Icon name={icon} color="white" boxSize={4} />
                        <Text color="white" fontWeight="bold" fontSize="sm">{label}</Text>
                    </HStack>
                    {loading ? (
                        <Spinner size="xs" color="whiteAlpha.700" />
                    ) : (
                        <Text color="white" fontWeight="extrabold" fontSize="2xl" lineHeight={1}>
                            {count}
                        </Text>
                    )}
                </HStack>
                <Text color="whiteAlpha.700" fontSize="xs" mt={1}>usuários online</Text>
            </Box>

            {/* Ações */}
            <VStack spacing={0} align="stretch">
                <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    leftIcon={<Icon name="logout" boxSize={3.5} />}
                    borderRadius={0}
                    justifyContent="flex-start"
                    px={4}
                    py={5}
                    fontSize="xs"
                    fontWeight="semibold"
                    isLoading={isLogoutLoading}
                    isDisabled={!!actionLoading && !isLogoutLoading}
                    onClick={onLogout}
                >
                    {logoutLabel}
                </Button>

                {hasMaintenance && (
                    <>
                        <Divider />
                        <HStack px={4} py={3} justify="space-between">
                            <HStack spacing={2}>
                                <Icon
                                    name={maintenanceValue ? 'plugs-bold' : 'plugs-connected-bold'}
                                    boxSize={3.5}
                                    color={maintenanceValue ? `${color}.500` : 'gray.400'}
                                />
                                <Text fontSize="xs" fontWeight="semibold" color={maintenanceValue ? `${color}.600` : 'gray.500'}>
                                    {maintenanceLabel}
                                </Text>
                                {maintenanceValue && (
                                    <Badge colorScheme={color} fontSize="0.6rem" borderRadius="full" px={2}>ATIVA</Badge>
                                )}
                            </HStack>
                            {isMaintenanceLoading ? (
                                <Spinner size="xs" />
                            ) : (
                                <Switch
                                    size="sm"
                                    colorScheme={color}
                                    isChecked={!!maintenanceValue}
                                    isDisabled={!!actionLoading}
                                    onChange={onToggleMaintenance}
                                />
                            )}
                        </HStack>
                    </>
                )}
            </VStack>
        </Box>
    )
}

SystemCard.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    count: PropTypes.number,
    gradient: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    cardBg: PropTypes.string.isRequired,
    cardBorder: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    actionLoading: PropTypes.string,
    onLogout: PropTypes.func.isRequired,
    logoutKey: PropTypes.string.isRequired,
    logoutLabel: PropTypes.string.isRequired,
    maintenanceValue: PropTypes.any,
    onToggleMaintenance: PropTypes.func,
    maintenanceKey: PropTypes.string.isRequired,
    maintenanceLabel: PropTypes.string.isRequired,
}

// ─── Linha da tabela ─────────────────────────────────────────────────────────

const SessionRow = ({ data, onRefresh, isProtected, onProtect }) => {
    const navigate = useNavigate()
    const toast = useToast()
    const [loading, setLoading] = useState(false)

    const getMap = (type) => {
        if (type?.includes('Employee'))    return { t: 'employees',   l: 'Backoffice',  c: 'purple', api: 'employee',    icon: 'employee'    }
        if (type?.includes('Customer'))    return { t: 'consultants', l: 'Consultores', c: 'orange', api: 'consultant',  icon: 'consultant'  }
        if (type?.includes('Distributor')) return { t: 'distributors',l: 'Núcleos',     c: 'blue',   api: 'distributor', icon: 'distributor' }
        return { t: 'common', l: 'Outro', c: 'gray', api: 'common', icon: 'user' }
    }

    const map    = getMap(data.tokenable_type)
    const active = data.is_active

    const handleLogout = async (e) => {
        e.stopPropagation()
        setLoading(true)
        try {
            await API.post(`/sessions/${map.api}/${data.tokenable_id}/logout`)
            toast({ title: 'Sessão encerrada', status: 'success' })
            onRefresh()
        } catch {
            toast({ title: 'Erro ao encerrar', status: 'error' })
        }
        setLoading(false)
    }

    const handleProtect = (e) => {
        e.stopPropagation()
        onProtect(data)
    }

    return (
        <Row onClick={() => navigate(`/${map.t}/${data.tokenable_id}`)} opacity={active ? 1 : 0.6} cursor="pointer">
            <Td>
                <Profile name={data.user_name} subtitle={data.user_email} />
            </Td>
            <Td>
                <Badge colorScheme={map.c} variant="subtle" borderRadius="full" px={3}>
                    <Icon name={map.icon} size="12px" mr={2} />
                    {map.l}
                </Badge>
            </Td>
            <Td fontSize="xs">
                {active ? (
                    <HStack spacing={1}>
                        <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
                        <Text color="green.500" fontWeight="bold">Online {Moment(data.last_used_at).fromNow()}</Text>
                    </HStack>
                ) : (
                    <Text color="gray.500">Desconectado {Moment(data.disconnected_at).fromNow()}</Text>
                )}
            </Td>
            <Td textAlign="right">
                <HStack spacing={1} justify="flex-end">
                    {!isProtected && (
                        <Tooltip label="Proteger: nunca deslogar em massa" fontSize="xs" hasArrow>
                            <ChakraIconButton
                                icon={<Icon name="roles" boxSize={3.5} />}
                                size="sm"
                                variant="ghost"
                                colorScheme="teal"
                                aria-label="Proteger usuário"
                                onClick={handleProtect}
                            />
                        </Tooltip>
                    )}
                    {isProtected && (
                        <Tooltip label="Usuário protegido" fontSize="xs" hasArrow>
                            <Box display="inline-flex" alignItems="center" px={2}>
                                <Icon name="identity" boxSize={4} color="teal.500" />
                            </Box>
                        </Tooltip>
                    )}
                    {active && (
                        <Button
                            leftIcon={<Icon name="plugs-bold" />}
                            colorScheme="red"
                            variant="ghost"
                            size="sm"
                            isLoading={loading}
                            onClick={handleLogout}
                        >
                            Deslogar
                        </Button>
                    )}
                </HStack>
            </Td>
        </Row>
    )
}

SessionRow.propTypes = {
    data: PropTypes.object.isRequired,
    onRefresh: PropTypes.func.isRequired,
    isProtected: PropTypes.bool,
    onProtect: PropTypes.func.isRequired,
}
